import { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { CreditCard, RotateCcw, History, Terminal, Menu, X, Settings, LogOut, Clock, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const allNavItems = [
  { path: "/", label: "Payment", icon: CreditCard, roles: ["admin", "pos"] },
  { path: "/moto", label: "MOTO", icon: Phone, roles: ["admin"] },
  { path: "/refund", label: "Refund", icon: RotateCcw, roles: ["admin"] },
  { path: "/history", label: "History", icon: History, roles: ["admin"] },
  { path: "/settings", label: "Settings", icon: Settings, roles: ["admin"] },
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const [terminalStatus, setTerminalStatus] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(null);

  // Filter nav items based on user role
  const navItems = allNavItems.filter(item => item.roles.includes(user?.role));

  useEffect(() => {
    const fetchTerminalStatus = async () => {
      try {
        const response = await axios.get(`${API}/terminal/status`);
        setTerminalStatus(response.data);
      } catch (error) {
        console.error("Failed to fetch terminal status:", error);
      }
    };
    fetchTerminalStatus();
  }, []);

  // Redirect POS users away from restricted pages
  useEffect(() => {
    if (user?.role === "pos" && ["/refund", "/history", "/settings"].includes(location.pathname)) {
      navigate("/");
    }
  }, [location.pathname, user?.role, navigate]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Admin session countdown timer
  useEffect(() => {
    if (user?.role === "admin" && user?.expires_at) {
      const updateTimeLeft = () => {
        const now = new Date();
        const expiresAt = new Date(user.expires_at);
        const diff = Math.max(0, Math.floor((expiresAt - now) / 1000));
        
        if (diff === 0) {
          logout();
        } else {
          setSessionTimeLeft(diff);
        }
      };

      updateTimeLeft();
      const interval = setInterval(updateTimeLeft, 1000);
      return () => clearInterval(interval);
    } else {
      setSessionTimeLeft(null);
    }
  }, [user, logout]);

  const formatTimeLeft = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const NavContent = ({ isMobile = false }) => (
    <>
      {/* Logo/Brand */}
      <div className="p-4 sm:p-6">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center overflow-hidden">
            <img src="/logo.png" alt="GeekTickets" className="w-full h-full object-contain" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-base sm:text-lg tracking-tight">GeekTickets</h1>
            <p className="text-xs text-muted-foreground">POS</p>
          </div>
        </div>
      </div>

      <Separator />

      {/* User Role Badge */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <Badge 
            variant="outline" 
            className={`text-xs ${isAdmin 
              ? "bg-purple-100 text-purple-800 border-purple-200" 
              : "bg-blue-100 text-blue-800 border-blue-200"
            }`}
            data-testid="user-role-badge"
          >
            {isAdmin ? "Admin" : "POS User"}
          </Badge>
          {sessionTimeLeft !== null && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" />
              <span className={sessionTimeLeft < 60 ? "text-red-500 font-medium" : ""}>
                {formatTimeLeft(sessionTimeLeft)}
              </span>
            </div>
          )}
        </div>
      </div>

      <Separator />

      {/* Navigation */}
      <nav className="flex-1 p-3 sm:p-4">
        <ul className="space-y-1 sm:space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={() => isMobile && setMobileMenuOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`
                    flex items-center gap-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg text-sm font-medium
                    transition-all duration-200 active:scale-95
                    ${isActive 
                      ? "bg-primary text-primary-foreground shadow-sm" 
                      : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      <Separator />

      {/* Terminal Status */}
      <div className="p-3 sm:p-4">
        <div className="bg-muted/50 rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Terminal
            </span>
            <Badge 
              variant="outline" 
              className={`text-xs ${terminalStatus?.api_configured 
                ? "bg-green-100 text-green-800 border-green-200" 
                : "bg-yellow-100 text-yellow-800 border-yellow-200"
              }`}
              data-testid="terminal-status-badge"
            >
              {terminalStatus?.api_configured ? "Connected" : "Not Configured"}
            </Badge>
          </div>
          <p 
            className="font-mono text-xs text-muted-foreground break-all leading-relaxed"
            data-testid="terminal-id-display"
          >
            {terminalStatus?.terminal_id || "Loading..."}
          </p>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <span className="font-medium">Currency:</span>
              <span className="font-mono">EUR</span>
            </div>
            <Badge 
              variant="outline" 
              className={`text-xs ${terminalStatus?.api_environment === "production" 
                ? "bg-red-100 text-red-800 border-red-200" 
                : "bg-yellow-100 text-yellow-800 border-yellow-200"
              }`}
            >
              {terminalStatus?.api_environment === "production" ? "Live" : "Sandbox"}
            </Badge>
          </div>
        </div>
      </div>

      <Separator />

      {/* Logout Button */}
      <div className="p-3 sm:p-4">
        <Button 
          variant="outline" 
          className="w-full justify-start gap-2 text-muted-foreground hover:text-foreground"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </Button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:w-60 lg:w-72 flex-col bg-card border-r border-border fixed h-full">
        <NavContent />
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-sm border-b border-border">
        <div className="flex items-center justify-between p-3 sm:p-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden">
              <img src="/logo.png" alt="GeekTickets" className="w-full h-full object-contain" />
            </div>
            <span className="font-heading font-bold text-sm sm:text-base">GeekTickets</span>
          </div>
          <div className="flex items-center gap-2">
            {sessionTimeLeft !== null && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                <span className={sessionTimeLeft < 60 ? "text-red-500 font-medium" : ""}>
                  {formatTimeLeft(sessionTimeLeft)}
                </span>
              </div>
            )}
            <Badge 
              variant="outline" 
              className={`text-xs ${isAdmin 
                ? "bg-purple-100 text-purple-800 border-purple-200" 
                : "bg-blue-100 text-blue-800 border-blue-200"
              }`}
            >
              {isAdmin ? "Admin" : "POS"}
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
              className="h-9 w-9"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside 
        className={`
          md:hidden fixed top-0 left-0 h-full w-64 bg-card border-r border-border z-50
          transform transition-transform duration-200 ease-in-out
          ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        <NavContent isMobile={true} />
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-60 lg:ml-72 min-h-screen">
        <div className="pt-14 md:pt-0">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
